function StepIndicator({ state }) {
  if (state === "completed") {
    return (
      <span className="relative grid h-6 w-6 place-items-center rounded-full bg-primary">
        <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
          <path
            d="M16.25 6.25L8.75 13.75L5 10"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="sr-only">Completed</span>
      </span>
    );
  }

  if (state === "current") {
    return (
      <span className="relative grid h-6 w-6 place-items-center rounded-full bg-primary">
        <span
          className="absolute -inset-1.5 rounded-full border-2 border-primary/60"
          aria-hidden="true"
        />
        <span className="h-2.5 w-2.5 rounded-full bg-white" aria-hidden="true" />
        <span className="sr-only">Current</span>
      </span>
    );
  }

  return (
    <span className="relative grid h-6 w-6 place-items-center rounded-full bg-slate-200">
      <span className="h-2.5 w-2.5 rounded-full bg-white/70" aria-hidden="true" />
      <span className="sr-only">Upcoming</span>
    </span>
  );
}

export function ProgressTracker({ steps, currentStepId, onStepClick, getStepCompletion }) {
  const currentIdx = Math.max(0, steps.findIndex((s) => s.id === currentStepId));

  return (
    <nav aria-label="Progress" className="w-full rounded-2xl bg-white px-4 py-6 md:px-8">
      <ol className="flex items-start">
        {steps.map((step, idx) => {
          const isCurrent = idx === currentIdx;

          const isCompleted =
            idx < currentIdx || (getStepCompletion ? !!getStepCompletion(step) : false);

          const state = isCompleted ? "completed" : isCurrent ? "current" : "upcoming";

          const leftFilled = idx !== 0 && (isCompleted || isCurrent);
          const rightFilled = idx !== steps.length - 1 && isCompleted;

          return (
            <li
              key={step.id}
              className="relative flex min-w-0 flex-1 flex-col items-center"
              aria-current={isCurrent ? "step" : undefined}
            >
              {/* Put the connectors + dot in a fixed-height row so the line always stays aligned */}
              <div className="flex w-full items-center">
                <div className="flex-1">
                  {idx !== 0 && (
                    <div
                      className={`h-1 w-full rounded-full ${leftFilled ? "bg-primary" : "bg-slate-200"}`}
                      aria-hidden="true"
                    />
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onStepClick?.(step.id)}
                  className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
                  aria-label={`Ga naar stap: ${step.title ?? step.name}`}
                >
                  <StepIndicator state={state} />
                </button>

                <div className="flex-1">
                  {idx !== steps.length - 1 && (
                    <div
                      className={`h-1 w-full rounded-full ${rightFilled ? "bg-primary" : "bg-slate-200"}`}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>

              <div className="mt-4 w-full px-2 text-center md:px-4">
                <div
                  className={[
                    "text-sm font-semibold",
                    isCurrent ? "text-primary" : isCompleted ? "text-slate-900" : "text-slate-500",
                  ].join(" ")}
                >
                  {step.title ?? step.name}
                </div>

                {step.description || step.content ? (
                  <div
                    className={[
                      "mt-1 text-xs leading-5",
                      isCurrent ? "text-primary/80" : isCompleted ? "text-slate-600" : "text-slate-400",
                    ].join(" ")}
                  >
                    {step.description ?? step.content}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}